/**
 * Brand Minimum Order Fee — ChefStop (Minimog / CRO GURU theme + Rebuy Smart Cart)
 * --------------------------------------------------------------------------------
 * Rules come from #brand-fee-config (shop metafield brand_fees.config):
 * {
 *   "rules": [
 *     { "vendor": "Omcan", "minimum": 200.00, "fee": 35.00, "variant_id": 1234567890 }
 *   ]
 * }
 *
 * Logic per rule:
 *   - Brand subtotal = sum of original_line_price (PRE-discount) for lines
 *     where item.vendor === rule.vendor (fee lines excluded).
 *   - 0 < subtotal < minimum  -> fee variant qty must be 1
 *   - otherwise               -> fee variant qty must be 0
 *
 * Enforcement at checkout is handled by the cart validation function.
 * This script keeps the cart UI correct and self-heals cart changes from apps.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('brand-fee-config');
  if (!configEl) return;

  var RULES;
  try {
    RULES = (JSON.parse(configEl.textContent).rules || []).filter(function (r) {
      return r && r.vendor && r.variant_id && Number(r.minimum) > 0;
    });
  } catch (e) {
    console.error('[brand-fee] invalid config JSON', e);
    return;
  }

  if (!RULES.length) return;

  var FEE_VARIANT_IDS = RULES.map(function (r) {
    return Number(r.variant_id);
  });

  var busy = false;
  var pendingRun = false;
  var debounceTimer = null;

  function isFeeLine(item) {
    return FEE_VARIANT_IDS.indexOf(Number(item.variant_id)) !== -1;
  }

  function computePlan(cart) {
    var plan = { adds: [], changes: [], updates: {}, changed: false };

    RULES.forEach(function (rule) {
      var subtotalCents = 0;
      var feeLine = null;
      var ruleVariantId = Number(rule.variant_id);

      cart.items.forEach(function (item) {
        if (Number(item.variant_id) === ruleVariantId) {
          feeLine = item;
          return;
        }

        if (isFeeLine(item)) return;

        if ((item.vendor || '') === rule.vendor) {
          // original_line_price = pre-discount line total, in cents
          subtotalCents += Number(item.original_line_price) || 0;
        }
      });

      var minimumCents = Math.round(Number(rule.minimum) * 100);
      var needsFee = subtotalCents > 0 && subtotalCents < minimumCents;
      var message = rule.message || (
        'Applies to ' + rule.vendor + ' orders under $' + Number(rule.minimum).toFixed(2) + '.'
      );

      if (needsFee && !feeLine) {
        // Add with message as a hidden line property (renders in Rebuy template)
        plan.adds.push({
          id: ruleVariantId,
          quantity: 1,
          properties: { _fee_message: message }
        });
        plan.changed = true;
      } else if (!needsFee && feeLine) {
        plan.updates[String(ruleVariantId)] = 0;
        plan.changed = true;
      } else if (needsFee && feeLine) {
        var hasMsg = feeLine.properties && feeLine.properties._fee_message === message;
        if (Number(feeLine.quantity) !== 1 || !hasMsg) {
          // Fix qty and/or stamp the message onto an existing fee line
          plan.changes.push({
            id: feeLine.key,
            quantity: 1,
            properties: { _fee_message: message }
          });
          plan.changed = true;
        }
      }
    });

    return plan.changed ? plan : null;
  }

  function refreshThemeCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  }

  function refreshRebuyCart() {
    // Rebuy changes method names between versions. These calls are guarded,
    // so unknown methods safely do nothing.
    try {
      if (window.Rebuy && window.Rebuy.SmartCart) {
        var smartCart = window.Rebuy.SmartCart;

        ['refresh', 'render', 'update', 'sync'].forEach(function (method) {
          if (typeof smartCart[method] === 'function') {
            try { smartCart[method](); } catch (e) {}
          }
        });
      }
    } catch (e) {}

    document.dispatchEvent(new CustomEvent('rebuy:cart.refresh'));
    document.dispatchEvent(new CustomEvent('rebuy:smartcart.refresh'));

    setTimeout(lockFeeLinesInRebuy, 50);
    setTimeout(lockFeeLinesInRebuy, 250);
    setTimeout(lockFeeLinesInRebuy, 750);
  }

  function refreshCarts() {
    refreshThemeCart();
    refreshRebuyCart();
  }

  function lockFeeLinesInRebuy() {
    var feeItems = document.querySelectorAll([
      '.rebuy-cart__flyout-item.product-small-order-fee',
      '.rebuy-cart__flyout-item.tag-_brandfee',
      '.rebuy-cart__flyout-item.tag-_brand-fee',
      '.rebuy-cart__flyout-item[class*="product-small-order-fee"]'
    ].join(','));

    feeItems.forEach(function (item) {
      item.setAttribute('data-brand-fee-locked', 'true');

      var controls = item.querySelectorAll([
        '.rebuy-cart__flyout-item-remove',
        '.rebuy-cart__flyout-item-quantity',
        '.rebuy-cart__flyout-item-quantity-widget',
        '[class*="quantity"] button',
        '[class*="quantity"] input',
        'input[type="number"]',
        'button[aria-label*="increase" i]',
        'button[aria-label*="decrease" i]',
        'button[aria-label*="remove" i]',
        'button[title*="increase" i]',
        'button[title*="decrease" i]',
        'button[title*="remove" i]'
      ].join(','));

      controls.forEach(function (control) {
        control.setAttribute('aria-hidden', 'true');
        control.setAttribute('tabindex', '-1');
        control.style.setProperty('display', 'none', 'important');
        control.style.setProperty('visibility', 'hidden', 'important');
        control.style.setProperty('pointer-events', 'none', 'important');
      });

      var links = item.querySelectorAll('a[href*="/products/small-order-fee"]');
      links.forEach(function (link) {
        link.removeAttribute('href');
        link.style.setProperty('pointer-events', 'none', 'important');
        link.style.setProperty('cursor', 'default', 'important');
      });
    });
  }

  function reconcile() {
    if (busy) { pendingRun = true; return; }
    busy = true;

    fetch(window.Shopify.routes.root + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var plan = computePlan(cart);
        if (!plan) return null;

        var chain = Promise.resolve();

        // Removals / zeroing via update.js (batched in one call)
        if (Object.keys(plan.updates).length) {
          chain = chain.then(function () {
            return fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: plan.updates })
            }).then(function (r) {
              if (!r.ok) throw new Error('cart/update.js ' + r.status);
            });
          });
        }

        // Property/qty fixes on existing fee lines via change.js (keyed by line)
        plan.changes.forEach(function (change) {
          chain = chain.then(function () {
            return fetch(window.Shopify.routes.root + 'cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(change)
            }).then(function (r) {
              if (!r.ok) throw new Error('cart/change.js ' + r.status);
            });
          });
        });

        // New fee lines via add.js (carries the _fee_message property)
        if (plan.adds.length) {
          chain = chain.then(function () {
            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: plan.adds })
            }).then(function (r) {
              if (!r.ok) throw new Error('cart/add.js ' + r.status);
            });
          });
        }

        return chain.then(function () { refreshCarts(); });
      })
      .catch(function (e) { console.error('[brand-fee]', e); })
      .finally(function () {
        busy = false;
        if (pendingRun) {
          pendingRun = false;
          setTimeout(reconcile, 50);
        }
      });
  }

  function scheduleReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 250);
  }

  // Theme event: fires after Minimog renders cart changes.
  document.addEventListener('cart:updated', scheduleReconcile);
  document.addEventListener('cart:refresh', function () {
    setTimeout(lockFeeLinesInRebuy, 250);
  });

  // Rebuy may redraw its cart after our CSS/JS first runs. Keep the fee locked.
  if (document.body && window.MutationObserver) {
    var observer = new MutationObserver(function () {
      lockFeeLinesInRebuy();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Safety net: catch ANY cart mutation from theme, Rebuy, quick-buy, or apps.
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
    var p = origFetch.apply(this, arguments);

    if (isCartMutation && !busy) {
      p.then(function (res) {
        if (res.ok) scheduleReconcile();
      }).catch(function () {});
    }

    return p;
  };

  // Initial check on page load, including existing carts from prior sessions.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      lockFeeLinesInRebuy();
      scheduleReconcile();
    });
  } else {
    lockFeeLinesInRebuy();
    scheduleReconcile();
  }

})();
